// Import Chart.js and required types
import { ArcElement, Chart, DoughnutController, Legend, PieController, Tooltip } from 'chart.js';

// Register Chart.js components
Chart.register(DoughnutController, ArcElement, PieController, Tooltip, Legend);

// Create a pie chart
function createPieChart(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context could not be retrieved.');
  }

  const dataValues = [4, 9, 38, 27, 20]; // data values

  new Chart(ctx, {
    type: 'pie',
    data: {
      labels: ['7+ hours', 'Less than 1 hour', '1-3 hours', '3-5 hours', '5-7 hours'],
      datasets: [
        {
          data: dataValues, // percentages of each segment
          backgroundColor: ['#1476D8', '#009262', '#FFB200', '#7DCBD9', '#FD4B23'],
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      layout: {
        padding: {
          bottom: 40,
          top: 40,
        },
      },
      plugins: {
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (tooltipItem) {
              const { dataset } = tooltipItem;
              const { dataIndex } = tooltipItem;
              const currentValue = dataset.data[dataIndex];
              const total = dataset.data.reduce((acc, value) => acc + value, 0);
              const percentage = ((currentValue / total) * 100).toFixed(2); // Calculate percentage
              return ` ${percentage}%`;
            },
          },
        },
        legend: {
          display: true,
          position: 'bottom', // Places the legend to the side of the chart
          labels: {
            padding: 32,
          },
        },
      },
    },
  });
}

// Create a doughnut chart
function createDoughnutChart(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context could not be retrieved.');
  }

  const percentage = parseInt(canvas.getAttribute('data-percentage') || '0', 10);
  const color = canvas.getAttribute('data-color') || '#FF6347'; // Default color if not provided

  new Chart(ctx, {
    type: 'doughnut',
    data: {
      datasets: [
        {
          data: [percentage, 100 - percentage],
          backgroundColor: [color, '#E0E0E0'],
          hoverBackgroundColor: [color, '#E0E0E0'],
          borderWidth: 0,
          borderRadius: 0, // Optional: remove if rounded edges are not needed
        },
      ],
    },
    options: {
      cutout: '86%',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: false },
        legend: { display: false },
      },
      animation: {
        animateRotate: true,
        duration: 800,
      },
    },
  });

  const chartText = document.createElement('div');
  chartText.classList.add('heading-style-h2', 'position-absolute');
  chartText.innerText = `${percentage}%`;
  canvas.parentNode?.appendChild(chartText);
}

// Set up Intersection Observer for doughnut charts
function observeCharts(): void {
  const charts = document.querySelectorAll<HTMLCanvasElement>('.donut-chart');

  const observerOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '0px',
    threshold: 0.75, // Adjust as needed
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        createDoughnutChart(entry.target as HTMLCanvasElement);
        observer.unobserve(entry.target); // Stop observing once animated
      }
    });
  }, observerOptions);

  charts.forEach((chart) => {
    observer.observe(chart);
  });
}

// Initialization
(function () {
  function initializeAnimations() {
    if (window.animationsInitialized) {
      return;
    }

    window.animationsInitialized = true;

    // Initialize pie chart
    const pieCanvas = document.getElementById('myPieChart') as HTMLCanvasElement;
    if (pieCanvas) {
      createPieChart(pieCanvas);
    }

    // Initialize doughnut charts
    observeCharts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAnimations);
  } else {
    initializeAnimations();
  }
})();
